import { allergyDisplayName, allergyReactions, groupAllergyRecords } from '@liza/fhir-utils';
import type { WithId } from '@medplum/core';
import type { AllergyIntolerance } from '@medplum/fhirtypes';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AllergyGroupRow, type AllergyGroupRowProps } from './AllergyGroupRow';
import { RecordListItem } from './RecordListItem';

export type AllergiesListProps = Omit<AllergyGroupRowProps, 'group'> & {
  /** The patient's whole allergy list. Grouping a partial list gives wrong reactions and tiers. */
  allergies: WithId<AllergyIntolerance>[];
};

/**
 * One row per allergen rather than one per resource, highest severity first. Rows that several
 * source records collapsed into expand to those records; rows with no usable name sit under a
 * "needs review" heading; a "no known allergies" row is shown only as the empty state. Grouping
 * and ordering come from `groupAllergyRecords`.
 */
export const AllergiesList = ({ allergies, ...rowProps }: AllergiesListProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const { groups, needsReview, noKnownAllergies } = useMemo(() => groupAllergyRecords(allergies), [allergies]);
  const { onSelectAllergy, selectedAllergyId, onPromotionClick, getPromotionStatus } = rowProps;

  return (
    <div className="mx-auto w-full max-w-4xl">
      <div className="space-y-1">
        {groups.length > 0 || needsReview.length > 0 ? (
          <>
            {groups.map((group) => (
              <AllergyGroupRow key={group.key} group={group} {...rowProps} />
            ))}
            {needsReview.length > 0 && (
              <>
                <h3 className="px-3 pt-4 pb-1 text-xs font-medium tracking-wider text-muted-foreground uppercase">
                  {t('needsReview')}
                </h3>
                {needsReview.map((record) => {
                  const reactions = allergyReactions([record]);
                  return (
                    <RecordListItem
                      key={record.id}
                      testId="allergy-needs-review"
                      title={allergyDisplayName(record) ?? t('unknownAllergy')}
                      description={reactions.length > 0 ? `${t('reactions')}: ${reactions.join(', ')}` : undefined}
                      onClick={onSelectAllergy ? (): void => onSelectAllergy(record.id) : undefined}
                      selected={selectedAllergyId === record.id}
                      onPromotionClick={onPromotionClick ? (): void => onPromotionClick(record.id) : undefined}
                      promotionStatus={getPromotionStatus?.(record.id)}
                    />
                  );
                })}
              </>
            )}
          </>
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {noKnownAllergies ? t('noKnownAllergies') : t('noAllergiesFound')}
          </p>
        )}
      </div>
    </div>
  );
};
